
import { Language, Product, CaseStudy, Resource, Translations, Testimonial, Partner, BlogPost, FaqItem, Certificate } from './types';

// English (Source)
const EN_TRANSLATIONS = {
  common: {
    globalLeader: "Global Export Leader",
    catalogLabel: "Product Catalog",
    successStories: "Success Stories",
    viewAll: "View All Products",
    techSpecs: "Technical Specifications",
    relatedProducts: "Related Products",
    headquarters: "Headquarters",
    phoneLabel: "Phone",
    emailLabel: "Email",
    compareTitle: "Product Comparison",
    feature: "Feature",
    category: "Category",
    descriptionLabel: "Description",
    code: "Model",
    certifications: "Certifications & Quality",
    socialConnect: "Connect with Us"
  },
  header: {
    phone: "+86 13149898413",
    email: "meitaicekong@126.com",
    exportMsg: "Exporting to UAE, Saudi Arabia, Nigeria, Kenya, Indonesia, Vietnam"
  },
  nav: {
    home: "Home",
    products: "Products",
    cases: "Solutions",
    resources: "Technical Support",
    about: "About Us",
    contact: "Contact",
  },
  hero: {
    title: "MEITAI: Precision Engineering for Global Industry",
    subtitle: "Leading exporter of Coriolis Mass Flowmeters and industrial instrumentation to the Middle East, Africa, and Southeast Asia. Quality, Reliability, Accuracy.",
    cta: "Explore Catalog",
    image1: "/images/hero1.webp",
    image2: "/images/hero2.webp",
    image3: "/images/hero3.webp"
  },
  footer: {
    companyTitle: "MEITAI",
    companyDesc: "Trusted partner for industrial instrumentation across MENA and APAC regions. ISO 9001 Certified.",
    marketsTitle: "Global Markets",
    market1: "Middle East (UAE, KSA, Qatar)",
    market2: "Africa (Egypt, Nigeria, Kenya)",
    market3: "Southeast Asia (Vietnam, Indonesia)",
    contactTitle: "Contact Us",
    address: "Dalian, Liaoning, China",
    email: "meitaicekong@126.com",
    phone: "+86 13149898413",
    copyright: "MEITAI Instruments © 2024. All rights reserved.",
    categories: { pressure: "Pressure", temp: "Temperature", level: "Level", flow: "Flow", analysis: "Analysis" }
  },
  contactInfo: {
    sidebarTitle: "Get in Touch",
    sidebarText: "Reach out to our global sales team for technical support and inquiries.",
    companyName: "MEITAI Instruments Co.",
    address: "Dalian, Liaoning, China",
    phone: "+86 13149898413",
    email: "meitaicekong@126.com",
    whatsapp: "+86 13149898413",
    wechat: "13149898413",
    wechatQr: "https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=wxid_meitai",
    facebook: "#", instagram: "#", tiktok: "#"
  },
  ai: { title: "MEITAI Advisor", placeholder: "Ask anything...", disclaimer: "AI Assistant can help choose instruments." },
  stats: {
    countVal: "50+", countLabel: "Countries Exported",
    isoVal: "ISO 9001", isoLabel: "Certified Quality",
    hubsVal: "3", hubsLabel: "Global Hubs",
    clientsVal: "1000+", clientsLabel: "Happy Clients"
  },
  products: {
    title: "Our Products",
    inquire: "Inquire Now"
  },
  whyChooseUs: {
    title: "Why Choose Us",
    subtitle: "Delivering excellence in industrial instrumentation",
    items: {
      strength: { title: "R&D Strength", desc: "Continuous innovation in measurement technology." },
      inspection: { title: "Strict Inspection", desc: "Every product undergoes rigorous testing." },
      quality: { title: "Premium Quality", desc: "Built to last in harsh industrial environments." },
      application: { title: "Wide Application", desc: "Suitable for oil, gas, chemical, and water industries." },
      service: { title: "Global Service", desc: "24/7 support and fast delivery worldwide." }
    }
  },
  home: {
    partnersTitle: "Trusted by Industry Leaders",
    testimonialsTitle: "What Our Clients Say"
  },
  aboutPage: {
    title: "About Us",
    storyTitle: "Our Story",
    storyText: "MEITAI Instruments has been a leading provider of industrial measurement solutions for over two decades.",
    missionTitle: "Our Mission",
    missionText: "To provide accurate, reliable, and cost-effective instrumentation to global industries.",
    factoryTitle: "Our Factory",
    factoryText: "State-of-the-art manufacturing facility ensuring the highest quality standards."
  },
  catalog: {
    title: "Product Catalog",
    intro: "Explore our comprehensive range of industrial instruments."
  },
  selectionPage: {
    title: "Selection Guide",
    intro: "Find the right instrument for your specific application.",
    successMessage: "Your request has been submitted successfully.",
    backHome: "Back to Home",
    steps: { media: "Media", conditions: "Conditions", output: "Output" },
    fields: { productType: "Product Type", medium: "Medium", pipeSize: "Pipe Size", pressure: "Pressure", temperature: "Temperature", outputSignal: "Output Signal" },
    contactInfoTitle: "Contact Information",
    submit: "Submit Request",
    placeholders: { productType: "Select Product Type", medium: "e.g., Water, Oil, Gas", name: "Your Name", email: "Your Email" }
  },
  contact: {
    success: "Success",
    title: "Send us a message",
    name: "Name",
    email: "Email",
    message: "Message",
    submit: "Send Message"
  },
  productDetail: {
    featuresTitle: "Key Features",
    specsTitle: "Specifications",
    resourcesTitle: "Resources & Downloads",
    techSupportTitle: "Technical Support",
    inquireTitle: "Request a Quote",
    inquirePromise: "We will respond to your inquiry within 24 hours."
  },
  blogPage: {
    title: "Blog & News",
    intro: "Latest updates and industry insights.",
    readMore: "Read More"
  },
  faqPage: {
    title: "Frequently Asked Questions",
    intro: "Find answers to common questions about our products and services."
  },
  casesPage: {
    title: "Industry Solutions",
    intro: "Discover how our instruments are used across various industries.",
    filterAll: "All Industries",
    industries: {
      water: "Water Treatment",
      oil: "Oil & Gas",
      food: "Food & Beverage",
      hvac: "HVAC"
    }
  },
  resourcesPage: {
    title: "Technical Resources",
    intro: "Download manuals, datasheets, and software.",
    calcTitle: "Flow Calculator",
    calcDesc: "Calculate flow rate based on pipe size and velocity.",
    tabs: {
      docs: "Documents",
      blog: "Blog & News",
      selection: "Selection Guide",
      tools: "Tools",
      videos: "Videos",
      faq: "FAQ"
    }
  }
};

// Simplified Chinese
const ZH_TRANSLATIONS = {
  ...EN_TRANSLATIONS,
  common: { ...EN_TRANSLATIONS.common, globalLeader: "全球出口领军者", catalogLabel: "产品目录", successStories: "成功案例", viewAll: "查看所有产品", techSpecs: "技术参数", relatedProducts: "相关产品", headquarters: "总部地址", phoneLabel: "电话", emailLabel: "邮箱", certifications: "资质证书与质量保证", socialConnect: "社交媒体" },
  nav: { home: "首页", products: "产品中心", cases: "解决方案", resources: "技术支持", about: "关于我们", contact: "联系我们" },
  hero: { title: "美泰 (MEITAI) - 全球精密工业仪表", subtitle: "中东、非洲和东南亚质量流量计领先出口商。品质卓越，精准可靠。", cta: "浏览产品目录", image1: EN_TRANSLATIONS.hero.image1, image2: EN_TRANSLATIONS.hero.image2, image3: EN_TRANSLATIONS.hero.image3 },
  footer: { ...EN_TRANSLATIONS.footer, companyTitle: "美泰 (MEITAI)", companyDesc: "中东、非洲及亚太地区工业仪表值得信赖的合作伙伴。通过 ISO 9001 认证。", marketsTitle: "全球市场", contactTitle: "联系我们", copyright: "美泰仪表 © 2024. 版权所有.", categories: { pressure: "压力仪表", temp: "温度仪表", level: "液位仪表", flow: "流量仪表", analysis: "液体分析" } },
  contactInfo: { ...EN_TRANSLATIONS.contactInfo, sidebarTitle: "联系我们", sidebarText: "联系我们的全球销售团队获取技术支持和询价。", companyName: "美泰仪表 (MEITAI)" },
  ai: { title: "AI 技术助手", placeholder: "请输入您的问题...", disclaimer: "AI助手可为您提供选型建议。" },
  stats: {
    countVal: "50+", countLabel: "出口国家",
    isoVal: "ISO 9001", isoLabel: "质量认证",
    hubsVal: "3", hubsLabel: "全球服务中心",
    clientsVal: "1000+", clientsLabel: "满意客户"
  },
  products: {
    title: "我们的产品",
    inquire: "立即询价"
  },
  whyChooseUs: {
    title: "为什么选择我们",
    subtitle: "在工业仪表领域提供卓越服务",
    items: {
      strength: { title: "研发实力", desc: "在测量技术上不断创新。" },
      inspection: { title: "严格检验", desc: "每件产品都经过严格测试。" },
      quality: { title: "卓越品质", desc: "专为恶劣工业环境打造，经久耐用。" },
      application: { title: "广泛应用", desc: "适用于石油、天然气、化工和水处理行业。" },
      service: { title: "全球服务", desc: "提供全天候支持和全球快速交付。" }
    }
  },
  home: {
    partnersTitle: "行业领导者的信赖",
    testimonialsTitle: "客户评价"
  },
  aboutPage: {
    title: "关于我们",
    storyTitle: "我们的故事",
    storyText: "二十多年来，美泰仪表一直是工业测量解决方案的领先供应商。",
    missionTitle: "我们的使命",
    missionText: "为全球工业提供准确、可靠且具有成本效益的仪表。",
    factoryTitle: "我们的工厂",
    factoryText: "最先进的制造设施，确保最高质量标准。"
  },
  catalog: {
    title: "产品目录",
    intro: "探索我们全面的工业仪表系列。"
  },
  selectionPage: {
    title: "选型指南",
    intro: "为您的特定应用找到合适的仪表。",
    successMessage: "您的请求已成功提交。",
    backHome: "返回首页",
    steps: { media: "介质", conditions: "工况", output: "输出" },
    fields: { productType: "产品类型", medium: "介质", pipeSize: "管道尺寸", pressure: "压力", temperature: "温度", outputSignal: "输出信号" },
    contactInfoTitle: "联系信息",
    submit: "提交请求",
    placeholders: { productType: "选择产品类型", medium: "例如：水、油、气", name: "您的姓名", email: "您的邮箱" }
  },
  contact: {
    success: "成功",
    title: "给我们留言",
    name: "姓名",
    email: "邮箱",
    message: "留言内容",
    submit: "发送留言"
  },
  productDetail: {
    featuresTitle: "主要特点",
    specsTitle: "技术规格",
    resourcesTitle: "资源与下载",
    techSupportTitle: "技术支持",
    inquireTitle: "请求报价",
    inquirePromise: "我们将在24小时内回复您的询价。"
  },
  blogPage: {
    title: "博客与新闻",
    intro: "最新动态和行业见解。",
    readMore: "阅读更多"
  },
  faqPage: {
    title: "常见问题",
    intro: "查找有关我们产品和服务的常见问题解答。"
  },
  casesPage: {
    title: "行业解决方案",
    intro: "了解我们的仪表在各个行业的应用。",
    filterAll: "所有行业",
    industries: {
      water: "水处理",
      oil: "石油与天然气",
      food: "食品饮料",
      hvac: "暖通空调"
    }
  },
  resourcesPage: {
    title: "技术资源",
    intro: "下载手册、数据表和软件。",
    calcTitle: "流量计算器",
    calcDesc: "根据管道尺寸和流速计算流量。",
    tabs: {
      docs: "文档下载",
      blog: "博客与新闻",
      selection: "选型指南",
      tools: "实用工具",
      videos: "视频教程",
      faq: "常见问题"
    }
  }
};

// Arabic
const AR_TRANSLATIONS = {
  ...EN_TRANSLATIONS,
  common: { ...EN_TRANSLATIONS.common, globalLeader: "الرائد العالمي في التصدير", catalogLabel: "كتالوج المنتجات", successStories: "قصص النجاح", viewAll: "عرض جميع المنتجات", techSpecs: "المواصفات الفنية", headquarters: "المقر الرئيسي", phoneLabel: "الهاتف", emailLabel: "البريد الإلكتروني", certifications: "الشهادات والجودة", socialConnect: "تواصل معنا" },
  nav: { home: "الرئيسية", products: "المنتجات", cases: "الحلول", resources: "الدعم الفني", about: "من نحن", contact: "اتصل بنا" },
  hero: { title: "ميتاي: هندسة دقيقة للصناعة العالمية", subtitle: "المصدر الرائد لعدادات تدفق كوريوليس والأجهزة الصناعية إلى الشرق الأوسط وأفريقيا وجنوب شرق آسيا.", cta: "استكشاف الكتالوج", image1: EN_TRANSLATIONS.hero.image1, image2: EN_TRANSLATIONS.hero.image2, image3: EN_TRANSLATIONS.hero.image3 },
  footer: { ...EN_TRANSLATIONS.footer, companyTitle: "ميتاي", companyDesc: "شريك موثوق للأجهزة الصناعية في الشرق الأوسط وآسيا. حاصل على شهادة ISO 9001.", marketsTitle: "الأسواق العالمية", contactTitle: "اتصل بنا", copyright: "ميتاي للأجهزة © 2024. جميع الحقوق محفوظة.", categories: { pressure: "الضغط", temp: "الحرارة", level: "المستوى", flow: "التدفق", analysis: "التحليل" } },
  contactInfo: { ...EN_TRANSLATIONS.contactInfo, sidebarTitle: "تواصل معنا", sidebarText: "تواصل مع فريق المبيعات العالمي للحصول على الدعم الفني والاستفسارات.", companyName: "شركة ميتاي للأجهزة" },
  ai: { title: "مستشار ميتاي", placeholder: "اسأل أي شيء...", disclaimer: "المساعد الذكي يساعد في اختيار الأجهزة." },
  stats: { countVal: "50+", countLabel: "الدول المصدرة", isoVal: "ISO 9001", isoLabel: "جودة معتمدة", hubsVal: "3", hubsLabel: "مراكز عالمية", clientsVal: "1000+", clientsLabel: "عملاء سعداء" },
  products: { title: "منتجاتنا", inquire: "استفسر الآن" },
  whyChooseUs: { title: "لماذا تختارنا", subtitle: "نقدم التميز في الأجهزة الصناعية", items: { strength: { title: "قوة البحث والتطوير", desc: "ابتكار مستمر في تكنولوجيا القياس." }, inspection: { title: "فحص دقيق", desc: "كل منتج يخضع لاختبارات صارمة." }, quality: { title: "جودة ممتازة", desc: "مصممة لتدوم في البيئات الصناعية القاسية." }, application: { title: "تطبيقات واسعة", desc: "مناسبة لصناعات النفط والغاز والكيمياء والمياه." }, service: { title: "خدمة عالمية", desc: "دعم على مدار الساعة طوال أيام الأسبوع وتوصيل سريع حول العالم." } } },
  home: { partnersTitle: "موثوق بها من قبل قادة الصناعة", testimonialsTitle: "ماذا يقول عملاؤنا" },
  aboutPage: { title: "من نحن", storyTitle: "قصتنا", storyText: "شركة ميتاي للأجهزة هي مزود رائد لحلول القياس الصناعي لأكثر من عقدين.", missionTitle: "مهمتنا", missionText: "توفير أجهزة دقيقة وموثوقة وفعالة من حيث التكلفة للصناعات العالمية.", factoryTitle: "مصنعنا", factoryText: "منشأة تصنيع حديثة تضمن أعلى معايير الجودة." },
  catalog: { title: "كتالوج المنتجات", intro: "استكشف مجموعتنا الشاملة من الأجهزة الصناعية." },
  selectionPage: { title: "دليل الاختيار", intro: "اعثر على الجهاز المناسب لتطبيقك المحدد.", successMessage: "تم تقديم طلبك بنجاح.", backHome: "العودة إلى الرئيسية", steps: { media: "الوسيط", conditions: "الظروف", output: "الإخراج" }, fields: { productType: "نوع المنتج", medium: "الوسيط", pipeSize: "حجم الأنبوب", pressure: "الضغط", temperature: "درجة الحرارة", outputSignal: "إشارة الإخراج" }, contactInfoTitle: "معلومات الاتصال", submit: "إرسال الطلب", placeholders: { productType: "اختر نوع المنتج", medium: "على سبيل المثال، ماء، زيت، غاز", name: "اسمك", email: "بريدك الإلكتروني" } },
  contact: { success: "نجاح", title: "أرسل لنا رسالة", name: "الاسم", email: "البريد الإلكتروني", message: "رسالة", submit: "إرسال رسالة" },
  productDetail: { featuresTitle: "الميزات الرئيسية", specsTitle: "المواصفات", resourcesTitle: "الموارد والتنزيلات", techSupportTitle: "الدعم الفني", inquireTitle: "طلب عرض أسعار", inquirePromise: "سنرد على استفسارك في غضون 24 ساعة." },
  blogPage: { title: "المدونة والأخبار", intro: "آخر التحديثات ورؤى الصناعة.", readMore: "اقرأ المزيد" },
  faqPage: { title: "أسئلة مكررة", intro: "اعثر على إجابات للأسئلة الشائعة حول منتجاتنا وخدماتنا." },
  casesPage: { title: "حلول الصناعة", intro: "اكتشف كيف يتم استخدام أجهزتنا في مختلف الصناعات.", filterAll: "جميع الصناعات", industries: { water: "معالجة المياه", oil: "النفط والغاز", food: "المأكولات والمشروبات", hvac: "التدفئة والتهوية وتكييف الهواء" } },
  resourcesPage: { title: "الموارد الفنية", intro: "قم بتنزيل الكتيبات وأوراق البيانات والبرامج.", calcTitle: "حاسبة التدفق", calcDesc: "احسب معدل التدفق بناءً على حجم الأنبوب والسرعة.", tabs: { docs: "المستندات", blog: "المدونة والأخبار", selection: "دليل الاختيار", tools: "أدوات", videos: "مقاطع فيديو", faq: "الأسئلة الشائعة" } }
};

// French
const FR_TRANSLATIONS = {
  ...EN_TRANSLATIONS,
  common: { ...EN_TRANSLATIONS.common, globalLeader: "Leader Mondial de l'Export", catalogLabel: "Catalogue de Produits", successStories: "Réussites", viewAll: "Voir Tous les Produits", techSpecs: "Spécifications", headquarters: "Siège Social", phoneLabel: "Téléphone", emailLabel: "E-mail", certifications: "Qualité & Certificats", socialConnect: "Suivez-nous" },
  nav: { home: "Accueil", products: "Produits", cases: "Solutions", resources: "Support Technique", about: "À Propos", contact: "Contact" },
  hero: { title: "MEITAI : Précision pour l'Industrie Mondiale", subtitle: "Premier exportateur de débitmètres Coriolis et d'instrumentation vers le Moyen-Orient, l'Afrique et l'Asie.", cta: "Explorer le Catalogue", image1: EN_TRANSLATIONS.hero.image1, image2: EN_TRANSLATIONS.hero.image2, image3: EN_TRANSLATIONS.hero.image3 },
  footer: { ...EN_TRANSLATIONS.footer, companyTitle: "MEITAI", companyDesc: "Partenaire de confiance pour l'instrumentation industrielle. Certifié ISO 9001.", marketsTitle: "Marchés Mondiaux", contactTitle: "Contactez-nous", copyright: "MEITAI Instruments © 2024. Tous droits réservés.", categories: { pressure: "Pression", temp: "Température", level: "Niveau", flow: "Débit", analysis: "Analyse" } },
  contactInfo: { ...EN_TRANSLATIONS.contactInfo, sidebarTitle: "Contactez-nous", sidebarText: "Contactez notre équipe de vente mondiale pour une assistance technique et des demandes de renseignements.", companyName: "MEITAI Instruments Co." },
  ai: { title: "Conseiller MEITAI", placeholder: "Posez votre question...", disclaimer: "L'IA vous aide à choisir vos instruments." },
  stats: { countVal: "50+", countLabel: "Pays Exportés", isoVal: "ISO 9001", isoLabel: "Qualité Certifiée", hubsVal: "3", hubsLabel: "Centres Mondiaux", clientsVal: "1000+", clientsLabel: "Clients Satisfaits" },
  products: { title: "Nos Produits", inquire: "Demander" },
  whyChooseUs: { title: "Pourquoi Nous Choisir", subtitle: "Excellence en instrumentation industrielle", items: { strength: { title: "R&D", desc: "Innovation continue en technologie de mesure." }, inspection: { title: "Inspection Stricte", desc: "Chaque produit subit des tests rigoureux." }, quality: { title: "Qualité Supérieure", desc: "Conçu pour durer dans des environnements industriels difficiles." }, application: { title: "Large Application", desc: "Convient aux industries pétrolière, gazière, chimique et de l'eau." }, service: { title: "Service Mondial", desc: "Support 24/7 et livraison rapide partout dans le monde." } } },
  home: { partnersTitle: "Approuvé par les Leaders", testimonialsTitle: "Avis Clients" },
  aboutPage: { title: "À Propos", storyTitle: "Notre Histoire", storyText: "MEITAI Instruments est l'un des principaux fournisseurs de solutions de mesure industrielle depuis plus de deux décennies.", missionTitle: "Notre Mission", missionText: "Fournir des instruments précis, fiables et rentables aux industries mondiales.", factoryTitle: "Notre Usine", factoryText: "Une installation de fabrication à la pointe de la technologie garantissant les normes de qualité les plus élevées." },
  catalog: { title: "Catalogue de Produits", intro: "Découvrez notre gamme complète d'instruments." },
  selectionPage: { title: "Guide de Sélection", intro: "Trouvez le bon instrument pour votre application spécifique.", successMessage: "Votre demande a été soumise avec succès.", backHome: "Retour à l'Accueil", steps: { media: "Média", conditions: "Conditions", output: "Sortie" }, fields: { productType: "Type de Produit", medium: "Média", pipeSize: "Taille du Tuyau", pressure: "Pression", temperature: "Température", outputSignal: "Signal de Sortie" }, contactInfoTitle: "Informations de Contact", submit: "Soumettre", placeholders: { productType: "Sélectionnez le type", medium: "ex: Eau, Huile", name: "Votre Nom", email: "Votre E-mail" } },
  contact: { success: "Succès", title: "Envoyez-nous un message", name: "Nom", email: "E-mail", message: "Message", submit: "Envoyer le Message" },
  productDetail: { featuresTitle: "Caractéristiques Principales", specsTitle: "Spécifications", resourcesTitle: "Ressources & Téléchargements", techSupportTitle: "Support Technique", inquireTitle: "Demander un Devis", inquirePromise: "Nous répondrons à votre demande dans les 24 heures." },
  blogPage: { title: "Blog & Actualités", intro: "Dernières mises à jour et informations de l'industrie.", readMore: "Lire la Suite" },
  faqPage: { title: "Foire Aux Questions", intro: "Trouvez des réponses aux questions courantes sur nos produits et services." },
  casesPage: { title: "Solutions Industrielles", intro: "Découvrez comment nos instruments sont utilisés dans diverses industries.", filterAll: "Toutes les Industries", industries: { water: "Traitement de l'Eau", oil: "Pétrole & Gaz", food: "Alimentation", hvac: "CVC" } },
  resourcesPage: { title: "Ressources Techniques", intro: "Téléchargez des manuels, fiches techniques et logiciels.", calcTitle: "Calculateur de Débit", calcDesc: "Calculez le débit en fonction de la taille du tuyau.", tabs: { docs: "Documents", blog: "Blog", selection: "Guide", tools: "Outils", videos: "Vidéos", faq: "FAQ" } }
};

// Russian
const RU_TRANSLATIONS = {
  ...EN_TRANSLATIONS,
  common: { ...EN_TRANSLATIONS.common, globalLeader: "Мировой Лидер Экспорта", catalogLabel: "Каталог Продукции", successStories: "История Успеха", viewAll: "Все Продукты", techSpecs: "Технические Данные", headquarters: "Штаб-квартира", phoneLabel: "Телефон", emailLabel: "Email", certifications: "Сертификаты и Качество", socialConnect: "Свяжитесь с нами" },
  nav: { home: "Главная", products: "Продукция", cases: "Решения", resources: "Техподдержка", about: "О Нас", contact: "Контакты" },
  hero: { title: "MEITAI: Точное Машиностроение для Мира", subtitle: "Ведущий экспортер расходомеров Кориолиса и промышленного оборудования в страны Ближнего Востока, Африки и ЮВА.", cta: "Открыть Каталог", image1: EN_TRANSLATIONS.hero.image1, image2: EN_TRANSLATIONS.hero.image2, image3: EN_TRANSLATIONS.hero.image3 },
  footer: { ...EN_TRANSLATIONS.footer, companyTitle: "MEITAI", companyDesc: "Надежный партнер в области КИПиА. Сертификат ISO 9001.", marketsTitle: "Мировые Рынки", contactTitle: "Контакты", copyright: "MEITAI Instruments © 2024. Все права защищены.", categories: { pressure: "Давление", temp: "Температура", level: "Уровень", flow: "Расход", analysis: "Анализ" } },
  contactInfo: { ...EN_TRANSLATIONS.contactInfo, sidebarTitle: "Свяжитесь с нами", sidebarText: "Свяжитесь с нашей глобальной командой продаж для получения технической поддержки и по вопросам сотрудничества.", companyName: "MEITAI Instruments Co." },
  ai: { title: "Консультант MEITAI", placeholder: "Задайте вопрос...", disclaimer: "ИИ поможет выбрать подходящий прибор." },
  stats: { countVal: "50+", countLabel: "Стран экспорта", isoVal: "ISO 9001", isoLabel: "Сертификация", hubsVal: "3", hubsLabel: "Глобальных центра", clientsVal: "1000+", clientsLabel: "Довольных Клиентов" },
  products: { title: "Наша продукция", inquire: "Запросить" },
  whyChooseUs: { title: "Почему мы", subtitle: "Превосходство в КИПиА", items: { strength: { title: "Разработки", desc: "Постоянные инновации в измерительной технике." }, inspection: { title: "Строгий Контроль", desc: "Каждый продукт проходит жесткие испытания." }, quality: { title: "Премиум Качество", desc: "Создан для работы в суровых условиях." }, application: { title: "Широкое Применение", desc: "Подходит для нефти, газа, химии и воды." }, service: { title: "Глобальный Сервис", desc: "Поддержка 24/7 и быстрая доставка по всему миру." } } },
  home: { partnersTitle: "Нам Доверяют Лидеры", testimonialsTitle: "Отзывы Клиентов" },
  aboutPage: { title: "О Нас", storyTitle: "Наша История", storyText: "MEITAI Instruments является ведущим поставщиком промышленных измерительных решений уже более двух десятилетий.", missionTitle: "Наша Миссия", missionText: "Обеспечить точными, надежными и экономичными приборами предприятия по всему миру.", factoryTitle: "Наш Завод", factoryText: "Современное производство, гарантирующее высочайшие стандарты качества." },
  catalog: { title: "Каталог Продукции", intro: "Изучите наш широкий ассортимент КИПиА." },
  selectionPage: { title: "Руководство по Выбору", intro: "Найдите подходящий прибор для вашей задачи.", successMessage: "Ваш запрос успешно отправлен.", backHome: "На Главную", steps: { media: "Среда", conditions: "Условия", output: "Выход" }, fields: { productType: "Тип Продукта", medium: "Среда", pipeSize: "Диаметр", pressure: "Давление", temperature: "Температура", outputSignal: "Выходной Сигнал" }, contactInfoTitle: "Контактная Информация", submit: "Отправить Запрос", placeholders: { productType: "Выберите тип продукта", medium: "Например, вода, масло, газ", name: "Ваше Имя", email: "Ваш Email" } },
  contact: { success: "Успех", title: "Напишите нам сообщение", name: "Имя", email: "Email", message: "Сообщение", submit: "Отправить Сообщение" },
  productDetail: { featuresTitle: "Особенности", specsTitle: "Характеристики", resourcesTitle: "Ресурсы и Загрузки", techSupportTitle: "Техническая Поддержка", inquireTitle: "Запросить Цену", inquirePromise: "Мы ответим на ваш запрос в течение 24 часов." },
  blogPage: { title: "Блог и Новости", intro: "Последние обновления и отраслевая аналитика.", readMore: "Читать Дальше" },
  faqPage: { title: "Частые Вопросы", intro: "Найдите ответы на часто задаваемые вопросы о нашей продукции и услугах." },
  casesPage: { title: "Отраслевые Решения", intro: "Узнайте, как наши приборы используются в различных отраслях.", filterAll: "Все Отрасли", industries: { water: "Водоподготовка", oil: "Нефть и Газ", food: "Пищевая", hvac: "ОВиК" } },
  resourcesPage: { title: "Технические Ресурсы", intro: "Скачайте инструкции, паспорта и ПО.", calcTitle: "Калькулятор Расхода", calcDesc: "Рассчитайте расход на основе диаметра трубы и скорости.", tabs: { docs: "Документы", blog: "Блог", selection: "Выбор", tools: "Инструменты", videos: "Видео", faq: "ЧаВо" } }
};

export const TRANSLATIONS: Translations = {
  [Language.EN]: EN_TRANSLATIONS,
  [Language.ZH]: ZH_TRANSLATIONS,
  [Language.AR]: AR_TRANSLATIONS,
  [Language.FR]: FR_TRANSLATIONS,
  [Language.RU]: RU_TRANSLATIONS,
};

export const CASES: CaseStudy[] = [
  {
    id: '1',
    title: { [Language.EN]: 'Water Treatment Upgrade', [Language.ZH]: '水处理厂升级', [Language.AR]: 'تطوير محطة المياه', [Language.FR]: 'Mise à niveau traitement des eaux', [Language.RU]: 'Модернизация водоканала' },
    location: { [Language.EN]: 'Cairo, Egypt', [Language.ZH]: '埃及开罗', [Language.AR]: 'القاهرة، مصر', [Language.FR]: 'Le Caire, Égypte', [Language.RU]: 'Каир, Египет' },
    description: { [Language.EN]: 'Supply and installation of 50 electromagnetic flowmeters.', [Language.ZH]: '供应并安装了50台电磁流量计。', [Language.AR]: 'توريد وتركيب 50 مقياس تدفق كهرومغناطيسي.', [Language.FR]: 'Fourniture et installation de 50 débitmètres.', [Language.RU]: 'Поставка и монтаж 50 расходомеров.' },
    industry: 'water',
    imageUrl: '/images/case1.webp'
  }
];

export const PRODUCTS: Product[] = [
  {
    id: '1',
    code: 'MT-CMF-01',
    name: { [Language.EN]: 'Mass Flow Meter', [Language.ZH]: '质量流量计', [Language.AR]: 'مقياس تدفق الكتلة', [Language.FR]: 'Débitmètre Massique', [Language.RU]: 'Массовый расходомер' },
    category: { [Language.EN]: 'Flow', [Language.ZH]: '流量仪表', [Language.AR]: 'التدفق', [Language.FR]: 'Débit', [Language.RU]: 'Расход' },
    description: { [Language.EN]: 'High precision measurement.', [Language.ZH]: '高精度流量测量。', [Language.AR]: 'قياس عالي الدقة.', [Language.FR]: 'Mesure haute précision.', [Language.RU]: 'Высокоточное измерение.' },
    specs: { [Language.EN]: ['Accuracy: ±0.1%'], [Language.ZH]: ['精度: ±0.1%'], [Language.AR]: ['الدقة: ±0.1%'], [Language.FR]: ['Précision: ±0.1%'], [Language.RU]: ['Точность: ±0.1%'] },
    imageUrl: '/images/hero1.webp',
    featured: true,
    features: { [Language.EN]: ["High Accuracy"], [Language.ZH]: ["高精度"], [Language.AR]: ["دقة عالية"], [Language.FR]: ["Haute Précision"], [Language.RU]: ["Высокая точность"] }
  }
];

export const RESOURCES: Resource[] = [];
export const TESTIMONIALS: Testimonial[] = [];
export const PARTNERS: Partner[] = [];
export const BLOG_POSTS: BlogPost[] = [];
export const FAQ_ITEMS: FaqItem[] = [];
export const CERTIFICATES: Certificate[] = [];
